import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Mail } from "lucide-react";

export function About() {
  return (
    <div className="anim-in">
      <div className="border-b border-border">
        <div className="container-x py-16">
          <p className="tech-label mb-3">Sobre a cadditive</p>
          <h1 className="text-5xl lg:text-6xl font-bold tracking-tighter max-w-4xl">
            Um estúdio técnico. <br />
            <span className="text-muted-foreground">Criativo por método.</span>
          </h1>
        </div>
      </div>
      <div className="container-x py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-6 text-base leading-relaxed">
          <p>
            A cadditive é um estúdio português de modelação 3D e fabrico aditivo baseado em Barcelos. O nome resulta da combinação entre <span className="italic">CAD</span> e <span className="italic">additive manufacturing</span>. Trabalhamos com particulares e empresas em todo o território nacional.
          </p>
          <p>
            Transformamos ideias, necessidades e problemas concretos em produtos físicos. Desenvolvemos protótipos, peças técnicas, produtos personalizados, pequenas séries, peças de substituição e produtos próprios.
          </p>
          <h3 className="text-2xl font-bold tracking-tight pt-4">Missão de acessibilidade</h3>
          <p>
            Acreditamos que produção acessível não precisa de significar baixa qualidade. Utilizamos fabrico aditivo para reduzir custos de desenvolvimento e produção, tornando acessórios técnicos e musicais mais acessíveis. Um iniciante não deve ser afastado por preços elevados.
          </p>
          <h3 className="text-2xl font-bold tracking-tight pt-4">Como trabalhamos</h3>
          <p>
            Cada projeto começa por uma análise técnica cuidadosa. Preparamos propostas transparentes, com prazos claros e materiais recomendados. Iteramos rapidamente e mantemos o cliente informado em cada etapa através do portal do cliente.
          </p>
        </div>
        <aside className="lg:col-span-4">
          <div className="border border-border p-6 sticky top-20 crosshair-corners space-y-4 bg-card">
            <p className="tech-label">Contactos</p>
            <div className="text-sm space-y-2">
              <div className="flex items-center gap-2"><MapPin size={14} className="text-brand" /> Barcelos, Portugal</div>
              <div className="flex items-center gap-2"><Phone size={14} className="text-brand" /> +351 912 000 000</div>
              <div className="flex items-center gap-2"><Mail size={14} className="text-brand" /> geral@cadditive.pt</div>
            </div>
            <Link to="/pedido-orcamento" className="btn-primary w-full justify-center mt-4">Pedir orçamento <ArrowRight size={14} /></Link>
          </div>
        </aside>
      </div>
    </div>
  );
}

export function Contact() {
  return (
    <div className="anim-in">
      <div className="container-x py-16">
        <p className="tech-label mb-3">Contactos</p>
        <h1 className="text-5xl lg:text-6xl font-bold tracking-tighter">Falamos?</h1>
        <p className="mt-6 text-muted-foreground max-w-2xl">
          Envie-nos uma mensagem, ficheiros ou apenas a sua ideia. Preferimos pedidos formais através do sistema de orçamento para conseguir dar-lhe uma resposta rigorosa e rastreável.
        </p>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          <div className="border border-border p-6 bg-card">
            <MapPin size={20} className="text-brand" />
            <h3 className="font-semibold mt-3">Morada</h3>
            <p className="text-sm text-muted-foreground mt-2">Barcelos, Portugal</p>
          </div>
          <div className="border border-border p-6 bg-card">
            <Phone size={20} className="text-brand" />
            <h3 className="font-semibold mt-3">Telefone / WhatsApp</h3>
            <p className="text-sm text-muted-foreground mt-2">+351 912 000 000</p>
          </div>
          <div className="border border-border p-6 bg-card">
            <Mail size={20} className="text-brand" />
            <h3 className="font-semibold mt-3">Email</h3>
            <p className="text-sm text-muted-foreground mt-2">geral@cadditive.pt</p>
          </div>
        </div>

        <div className="mt-14 flex gap-3">
          <Link to="/pedido-orcamento" className="btn-primary">Pedir orçamento <ArrowRight size={14} /></Link>
          <a href="mailto:geral@cadditive.pt" className="btn-outline">Enviar email</a>
        </div>
      </div>
    </div>
  );
}

const LEGAL_PAGES = {
  termos: {
    title: "Termos e condições",
    body: `Ao utilizar o website cadditive, aceita os presentes termos e condições. A cadditive presta serviços de modelação 3D, impressão 3D, prototipagem e desenvolvimento de produto. O cliente é responsável por assegurar que possui os direitos legais para utilização dos ficheiros submetidos. A confirmação da encomenda pressupõe a aceitação destes termos.`,
  },
  privacidade: {
    title: "Política de privacidade",
    body: `A cadditive trata dados pessoais em conformidade com o RGPD. Recolhemos apenas os dados necessários para o cumprimento do serviço (nome, email, telefone, morada, NIF quando aplicável). Os dados nunca são partilhados com terceiros para efeitos comerciais. O utilizador pode a qualquer momento solicitar o acesso, correção ou eliminação dos seus dados.`,
  },
  ficheiros: {
    title: "Política de ficheiros",
    body: `Os ficheiros enviados pelos clientes são armazenados de forma privada e apenas acessíveis pelo cliente e pela equipa técnica da cadditive. Não publicamos qualquer ficheiro ou projeto no portefólio sem autorização expressa. O cliente confirma no envio ser detentor dos direitos ou de autorização para utilização dos ficheiros. Retemos os ficheiros durante o período de execução do serviço e garantia; após esse período podem ser eliminados.`,
  },
  envios: {
    title: "Política de envios",
    body: `A cadditive envia para todo o território nacional através de transportadora (CTT ou equivalente). Prazos estimados: Portugal Continental 2-3 dias úteis, expresso 24h. Envios para Ilhas e União Europeia mediante orçamento. Também é possível recolha em Barcelos após combinação prévia.`,
  },
  devolucoes: {
    title: "Política de devoluções",
    body: `Produtos padrão gozam do direito legal de livre resolução de 14 dias. Produtos personalizados ou fabricados por encomenda não são elegíveis para devolução, exceto em caso de defeito. Caso identifique um defeito de produção, contacte-nos até 8 dias após a receção.`,
  },
};

export function LegalPage({ slug }) {
  const p = LEGAL_PAGES[slug];
  if (!p) return <div className="container-x py-20 tech-label">página não encontrada</div>;
  return (
    <div className="container-x py-16 max-w-3xl">
      <p className="tech-label mb-3">Legal · cadditive</p>
      <h1 className="text-4xl font-bold tracking-tighter">{p.title}</h1>
      <p className="mt-8 text-sm leading-relaxed whitespace-pre-line">{p.body}</p>
    </div>
  );
}
